import { useRouter } from "next/router";
import { format, subDays } from "date-fns";
import { useUserContext } from "../../../../contexts/UserProvider";
import { axiosApiInstance } from "../../../../pages/api/axios";
import { useMoneyHashQueryOnView } from "../../../../hooks/useMoneyHashQueryOnView";
import { type ErrorResponse } from "../../../../hooks/useMoneyHashQuery";
import { useFeatureFlags } from "../../../../hooks/query";

interface UseDatadogQueryOptions {
  chartType:
    | "CAPTURE_REVENUE"
    | "AUTHORIZED_REVENUE"
    | "REFUNDED_REVENUE"
    | "TOTAL_TRANSACTIONS"
    | "TOP_PROVIDERS"
    | "TOP_PAYMENT_METHODS"
    | "ERROR_CODES_COUNTS"
    | "DAILY_ERROR_CODES_COUNTS"
    | "BIN_BRANDS"
    | "BIN_ISSUING_COUNTRIES"
    | "BIN_TOP_ISSUERS"
    | "RECOVERY_RATE"
    | "OVERALL_SOFT_DECLINE_RECOVERY"
    | "SOFT_DECLINE_COVERAGE"
    | "INTENT_CONVERSION_RATE"
    | "PAYMENT_PERFORMANCE"
    | "TOTAL_DECLINES"
    | "CAPTURED_REVENUE_TOTAL"
    | "TOTAL_METRICS"
    | "PROVIDERS_ACCEPTANCE_RATE"
    | "PROVIDERS_SUCCESS_RATE"
    | "PROVIDERS_CAPTURE_RATE"
    | "METHODS_ACCEPTANCE_RATE"
    | "METHODS_SUCCESS_RATE"
    | "METHODS_CAPTURE_RATE"
    | "TOTAL_TRANSACTIONS_VOLUME"
    | "TOTAL_TRANSACTIONS_SUCCESSFUL"
    | "TOTAL_TRANSACTIONS_FAILED"
    | "TOTAL_ERROR_CODES"
    | "AUTHORIZED_REVENUE_TOTAL"
    | "REFUNDED_REVENUE_TOTAL"
    | "TOTAL_REVENUE"
    | "REVENUE_BY_PROVIDER"
    | "REVENUE_BY_METHOD";

  // Delay chart request by x milliseconds amount
  delayMs?: number;
}

export function useDatadogQuery<TData>({
  chartType,
  delayMs,
}: UseDatadogQueryOptions) {
  const router = useRouter();
  const {
    start_date,
    end_date,
    provider,
    account,
    initiation,
    organization,
    provider_live_status,
    currency,
    method,
  } = router.query;
  const { isMoneyHashUser, organizationId } = useUserContext();
  const { featureFlags } = useFeatureFlags();

  const isMartBackedChart = featureFlags.new_dashboard_rates_from_marts;
  const effectiveDelayMs = isMartBackedChart ? 0 : delayMs;

  return useMoneyHashQueryOnView<TData>({
    queryKey: [
      chartType,
      {
        start_date,
        end_date,
        organization,
        organizationId,
        provider,
        account,
        initiation,
        provider_live_status,
        currency,
        method,
        effectiveDelayMs,
      },
    ],
    queryFn: async ({ signal }) => {
      if (effectiveDelayMs) {
        await new Promise((resolve) => setTimeout(resolve, effectiveDelayMs));
      }

      const today = format(new Date(), "yyyy-MM-dd");
      const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");
      return axiosApiInstance
        .get("/d/metrics/analytics/", {
          signal,
          params: {
            chart: chartType,
            start_date: start_date || thirtyDaysAgo,
            end_date: end_date || today,
            organization: organization || organizationId,
            provider,
            account: account || undefined,
            initiation,
            provider_live_status,
            currency,
            method,
          },
        })
        .then((res) => res.data);
    },
    enabled: !isMoneyHashUser || (isMoneyHashUser && !!organizationId),
    retry: (failureCount, error: ErrorResponse) => {
      if (
        error?.response?.data?.status?.errors?.[0]?.end_date ===
        "maximum date range is 30 days"
      )
        return false;

      if (failureCount >= 2) return false;

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
